import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
  Stack,
  CircularProgress,
} from '@mui/material';
import Phone from '@mui/icons-material/Phone';
import Email from '@mui/icons-material/Email';
import LocationOn from '@mui/icons-material/LocationOn';
import Send from '@mui/icons-material/Send';
import CheckCircle from '@mui/icons-material/CheckCircle';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    // Mock API submission
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setSuccess(false), 5000);
    }, 1500);
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 70px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: '40px 16px', md: '60px 24px' },
        position: 'relative',
        background: '#0F2E2B',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(650px 450px at 85% -10%, rgba(212,162,76,0.14), transparent 60%),
            radial-gradient(550px 450px at -10% 80%, rgba(47,143,118,0.18), transparent 60%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Fraunces", serif',
              fontWeight: 700,
              color: '#F5F1E6',
              mb: 1.5,
            }}
          >
            Contact Us
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(245, 241, 230, 0.6)', maxWidth: '600px', mx: 'auto' }}>
            Have questions about our premium coupons or need support? Reach out to our team, and we will get back to you shortly.
          </Typography>
        </Box>



        <Grid container spacing={5} alignItems="flex-start">
          {/* LEFT COLUMN */}
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                background: "rgba(21,58,53,0.6)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(245,241,230,0.12)",
                borderRadius: 4,
                p: { xs: 3, md: 5 },
                width: "100%",
                height: "100%",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"Fraunces", serif',
                  color: "#F5F1E6",
                  fontWeight: 700,
                  textAlign: "center",
                  mb: 4,
                }}
              >
                Get in Touch
              </Typography>

              {success && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "rgba(47,143,118,.15)",
                    border: "1px solid #2F8F76",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <CheckCircle sx={{ color: "#2F8F76" }} />

                  <Typography sx={{ color: "#F5F1E6" }}>
                    Message sent successfully! We'll reply shortly.
                  </Typography>
                </Box>
              )}

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Name */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Full Name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      sx={inputStyles}
                    />
                  </Grid>

                  {/* Email */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="email"
                      name="email"
                      label="Email Address"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      sx={inputStyles}
                    />
                  </Grid>

                  {/* Subject */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      name="subject"
                      label="Subject"
                      placeholder="How can we help you?"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      sx={inputStyles}
                    />
                  </Grid>

                  {/* Message */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      name="message"
                      label="Message"
                      placeholder="Write your query details here..."
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      sx={inputStyles}
                    />
                  </Grid>

                  {/* Button */}
                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: {
                          xs: "center",
                          md: "flex-start",
                        },
                        mt: 1,
                      }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        endIcon={!loading && <Send />}
                        sx={{
                          width: {
                            xs: "100%",
                            sm: 220,
                          },
                          maxWidth: 220,
                          height: 58,
                          borderRadius: "50px",
                          backgroundColor: "#D4A24C",
                          color: "#0F2E2B",
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          textTransform: "none",
                          boxShadow: "none",
                          whiteSpace: "nowrap",

                          "&:hover": {
                            backgroundColor: "#E2B45E",
                            boxShadow: "none",
                          },

                          "&.Mui-disabled": {
                            backgroundColor: "rgba(212,162,76,.4)",
                            color: "#0F2E2B",
                          },
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={22} color="inherit" />
                        ) : (
                          "Send Message"
                        )}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>

          {/* RIGHT COLUMN */}
          <Grid size={{ xs: 12, md: 12 }}>
            <Stack spacing={3} height="100%">

              {/* Card: Address */}
              <Paper
                elevation={0}
                sx={{
                  background: 'rgba(21, 58, 53, 0.4)',
                  border: '1px solid rgba(245, 241, 230, 0.08)',
                  borderRadius: '20px',
                  padding: 3,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2.5,
                  width: '100%',
                }}
              >
                <Avatar sx={{ backgroundColor: 'rgba(212, 162, 76, 0.12)', color: '#D4A24C' }}>
                  <LocationOn />
                </Avatar>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle2" sx={{ color: '#D4A24C', fontWeight: 600, mb: 0.5 }}>
                    Office Address
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#F5F1E6', lineHeight: 1.6 }}>
                    Shop No.01, Jadhav Estate, Kolshet Rd, opp. Everest World, Thane - 400607, Maharashtra.
                  </Typography>
                </Box>
              </Paper>

              {/* Card: Contact Number */}
              <Paper
                elevation={0}
                sx={{
                  background: 'rgba(21, 58, 53, 0.4)',
                  border: '1px solid rgba(245, 241, 230, 0.08)',
                  borderRadius: '20px',
                  padding: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2.5,
                }}
              >
                <Avatar sx={{ backgroundColor: 'rgba(212, 162, 76, 0.12)', color: '#D4A24C' }}>
                  <Phone />
                </Avatar>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle2" sx={{ color: '#D4A24C', fontWeight: 600, mb: 0.5 }}>
                    Contact Number
                  </Typography>
                  <Typography
                    variant="body2"
                    component="a"
                    href="tel:+918369693745"
                    sx={{ color: '#F5F1E6', textDecoration: 'none', '&:hover': { color: '#D4A24C' } }}
                  >
                    +91 8369 693 745
                  </Typography>
                </Box>
              </Paper>

              {/* Card: Email ID */}
              <Paper
                elevation={0}
                sx={{
                  background: 'rgba(21, 58, 53, 0.4)',
                  border: '1px solid rgba(245, 241, 230, 0.08)',
                  borderRadius: '20px',
                  padding: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2.5,
                }}
              >
                <Avatar sx={{ backgroundColor: 'rgba(212, 162, 76, 0.12)', color: '#D4A24C' }}>
                  <Email />
                </Avatar>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle2" sx={{ color: '#D4A24C', fontWeight: 600, mb: 0.5 }}>
                    Email ID
                  </Typography>
                  <Typography
                    variant="body2"
                    component="a"
                    href="mailto:isoret.server@gmail.com"
                    sx={{ color: '#F5F1E6', textDecoration: 'none', '&:hover': { color: '#D4A24C' } }}
                  >
                    isoret.server@gmail.com
                  </Typography>
                </Box>
              </Paper>

            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

const inputStyles = {
  '& .MuiInputLabel-root': {
    color: 'rgba(245, 241, 230, 0.5)',
    '&.Mui-focused': {
      color: '#D4A24C',
    },
    '&.Mui-error': {
      color: '#E4572E',
    },
  },
  '& .MuiOutlinedInput-root': {
    color: '#F5F1E6',
    backgroundColor: 'rgba(15, 46, 43, 0.4)',
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: 'rgba(245, 241, 230, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(245, 241, 230, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#D4A24C',
      borderWidth: '1.5px',
    },
    '&.Mui-error fieldset': {
      borderColor: '#E4572E',
    },
  },
};
